import { mount, unmount } from 'svelte';
import StandaloneChat from './StandaloneChat.svelte';
export interface WidgetConfig {
	apiKey: string;
	tenantId: string;
	containerId?: string;
	baseUrl?: string;
	model?: string;
	initialMessage?: string;
	chatPlaceholder?: string;
	cssPath?: string; // Path to widget.css, defaults to auto-detection
}

/**
 * Widget instance - manages lifecycle of a single embedded chat widget
 */
class Widget {
	private shadowRoot: ShadowRoot;
	private svelteComponent: any;
	private container: HTMLElement;

	constructor(container: HTMLElement, config: Omit<WidgetConfig, 'containerId'>) {
		this.container = container;

		// Create Shadow DOM for style isolation
		this.shadowRoot = container.attachShadow({ mode: 'open' });

		// Inject CSS styles
		const styleSheet = document.createElement('link');
		styleSheet.rel = 'stylesheet';

		// Auto-detect CSS path if not provided
		if (config.cssPath) {
			styleSheet.href = config.cssPath;
		} else {
			// Find the widget script and use its directory
			const scripts = Array.from(document.querySelectorAll('script'));
			const widgetScript = scripts.find((s) => s.src.includes('widget'));
			if (widgetScript) {
				const scriptUrl = new URL(widgetScript.src);
				const basePath = scriptUrl.pathname.substring(0, scriptUrl.pathname.lastIndexOf('/'));
				styleSheet.href = `${scriptUrl.origin}${basePath}/widget.css`;
			} else {
				// Fallback to same directory as HTML
				styleSheet.href = './dist-widget/widget.css';
			}
		}

		this.shadowRoot.appendChild(styleSheet);

		styleSheet.onload = () => {
			console.log('Styles loaded! Mounting widget...');
			// Create mount point for Svelte component
			const mountPoint = document.createElement('div');
			mountPoint.id = 'widget-mount';
			mountPoint.style.width = '100%';
			mountPoint.style.height = '100%';
			this.shadowRoot.appendChild(mountPoint);
			// Mount Svelte component
			this.svelteComponent = mount(StandaloneChat, {
				target: mountPoint,
				props: {
					apiKey: config.apiKey,
					tenantId: config.tenantId,
					baseUrl: config.baseUrl,
					model: config.model,
					initialMessage: config.initialMessage,
					chatPlaceholder: config.chatPlaceholder
				}
			});
		};
	}

	/**
	 * Destroy the widget instance
	 */
	destroy() {
		if (this.svelteComponent) {
			unmount(this.svelteComponent);
		}

		this.shadowRoot.innerHTML = '';
	}
}

/**
 * Global BotbakeryWidget API - singleton that manages widget instances
 */
class BotbakeryWidgetSingleton {
	private instances: Map<string, Widget> = new Map();

	/**
	 * Initialize a new widget instance
	 */
	init(config: WidgetConfig): Widget {
		const containerId = config.containerId || 'botbakery-chat-widget';

		// Cleanup existing instance if re-initializing
		if (this.instances.has(containerId)) {
			this.instances.get(containerId)!.destroy();
		}

		// Find container element
		const container = document.getElementById(containerId);
		if (!container) {
			throw new Error(`Container element with id "${containerId}" not found`);
		}

		// Create widget instance
		const widget = new Widget(container, config);
		this.instances.set(containerId, widget);
		return widget;
	}

	/**
	 * Destroy a widget instance
	 */
	destroy(containerId: string) {
		const widget = this.instances.get(containerId);
		if (widget) {
			widget.destroy();
			this.instances.delete(containerId);
		}
	}
}

// Export singleton instance
export const BotbakeryWidget = new BotbakeryWidgetSingleton();
